/**
 * Popover — accessible click-/keyboard-triggered popover, built on Radix UI.
 *
 * Companion to `Tooltip`. Use a popover when:
 *   - The content is interactive (links, buttons, form fields).
 *   - Activation should be intentional (click / Enter) rather than incidental
 *     (hover).
 *   - The content should remain open after the trigger loses focus.
 *
 * Use `Tooltip` for non-interactive labels and short explanations.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { cn } from "../../styling/index.js";

export const Popover = PopoverPrimitive.Root;
export const PopoverTrigger = PopoverPrimitive.Trigger;
export const PopoverAnchor = PopoverPrimitive.Anchor;

export type PopoverContentProps = React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>;

export const PopoverContent = React.forwardRef<
  React.ComponentRef<typeof PopoverPrimitive.Content>,
  PopoverContentProps
>(function PopoverContent(
  { className, align = "center", sideOffset = 6, children, ...props },
  ref,
) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        className={cn(
          "z-50 w-72 rounded-md border border-neutral-300 bg-white p-4 text-sm leading-snug",
          "text-neutral-900 shadow-lg outline-none",
          "animate-in fade-in-0 zoom-in-95",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
          className,
        )}
        {...props}
      >
        {children}
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  );
});
